import React, { useState, useEffect } from 'react';
import { 
  getAppInfo, 
  getAppInfoLocalizations, 
  updateAppInfoLocalization, 
  updateAppStoreVersion
} from '../../services/api';

// Common locales for iOS apps (Apple App Store Connect format)
const AVAILABLE_LOCALES = [
  { code: 'ar-SA', name: 'Arabic' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh-Hans', name: 'Chinese (Simplified)' },
  { code: 'zh-Hant', name: 'Chinese (Traditional)' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'en-AU', name: 'English (Australia)' },
  { code: 'en-CA', name: 'English (Canada)' },
  { code: 'en-GB', name: 'English (U.K.)' },
  { code: 'en-US', name: 'English (U.S.)' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'de-DE', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'id', name: 'Indonesian' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ms', name: 'Malay' },
  { code: 'no', name: 'Norwegian' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'sv', name: 'Swedish' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'vi', name: 'Vietnamese' },
];

function AppInformation({ app, appDetails, currentVersion, versions, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [appInfo, setAppInfo] = useState(null);
  const [localizations, setLocalizations] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    subtitle: '',
    privacyPolicyUrl: '',
    copyright: '',
  });

  useEffect(() => {
    if (app) {
      // Clear existing data when switching apps to ensure fresh load
      setAppInfo(null);
      setLocalizations([]);
      setFormData({
        name: '',
        subtitle: '',
        privacyPolicyUrl: '',
        copyright: '',
      });
      loadAppInfo();
    }
  }, [app, currentVersion]);

  const loadAppInfo = async (forceRefresh = false) => {
    if (!forceRefresh && appInfo && localizations.length > 0) {
      // Skip loading if we already have data and it's not a force refresh
      return;
    }
    
    setLoading(true);
    try {
      console.log('Loading app info for app:', app.id, 'currentVersion:', currentVersion?.id, 'state:', currentVersion?.attributes?.appStoreState);
      const infoResponse = await getAppInfo(app.id);
      if (infoResponse.data.success && infoResponse.data.appInfos.length > 0) {
        const appInfos = infoResponse.data.appInfos;
        
        // Select the appropriate appInfo based on current version
        // Priority: Use appInfo that matches the current selected version
        let selectedAppInfo = null;
        
        if (currentVersion) {
          const currentVersionState = currentVersion.attributes?.appStoreState;
          console.log('Available appInfos:', appInfos.map(info => ({ 
            id: info.id, 
            state: info.attributes?.state,
            appStoreState: info.attributes?.appStoreState 
          })));
          
          // Priority 1: If current version is editable (PREPARE_FOR_SUBMISSION, READY_FOR_REVIEW, DEVELOPER_REJECTED),
          // use the editable appInfo (which should be the one with matching state or the latest editable one)
          if (['PREPARE_FOR_SUBMISSION', 'READY_FOR_REVIEW', 'DEVELOPER_REJECTED'].includes(currentVersionState)) {
            // Try to find appInfo in editable state
            selectedAppInfo = appInfos.find(info => 
              info.attributes?.appStoreState === 'PREPARE_FOR_SUBMISSION' ||
              info.attributes?.appStoreState === 'READY_FOR_REVIEW' ||
              info.attributes?.appStoreState === 'DEVELOPER_REJECTED'
            );
            console.log('Looking for editable appInfo for version state:', currentVersionState, 'found:', selectedAppInfo?.id);
          }
          
          // Priority 2: If current version is in review/published states, find matching appInfo
          if (!selectedAppInfo && ['WAITING_FOR_REVIEW', 'IN_REVIEW', 'PENDING_APPLE_RELEASE', 'READY_FOR_SALE'].includes(currentVersionState)) {
            selectedAppInfo = appInfos.find(info => 
              info.attributes?.appStoreState === currentVersionState
            );
            console.log('Looking for review/published appInfo for version state:', currentVersionState, 'found:', selectedAppInfo?.id);
          }
        }
        
        // Default fallback: use the first appInfo
        if (!selectedAppInfo) {
          console.log('No matching appInfo found, using first available');
          selectedAppInfo = appInfos[0];
        }
        
        console.log('Selected appInfo:', selectedAppInfo.id, 'appStoreState:', selectedAppInfo.attributes?.appStoreState);
        setAppInfo(selectedAppInfo);

        // Load localizations for the selected appInfo
        const locResponse = await getAppInfoLocalizations(selectedAppInfo.id);
        if (locResponse.data.success) {
          const locs = locResponse.data.localizations;
          console.log('Loaded localizations:', locs.map(l => l.attributes.locale));
          setLocalizations(locs);
          
          // Use the first available localization instead of hardcoding en-US
          if (locs.length > 0) {
            const firstLocalization = locs[0];
            console.log('Setting first/primary locale to:', firstLocalization.attributes.locale);
            setSelectedLocale(firstLocalization.attributes.locale);
            setFormData({
              name: firstLocalization.attributes.name || '',
              subtitle: firstLocalization.attributes.subtitle || '',
              privacyPolicyUrl: firstLocalization.attributes.privacyPolicyUrl || '',
              copyright: currentVersion?.attributes?.copyright || '',
            });
          }
        }
      }
    } catch (err) {
      console.error('Failed to load app info:', err);
    } finally {
      setLoading(false);
    }
  };

  const isAppInfoEditable = () => {
    if (!currentVersion) return false;
    
    // Check if current version allows editing
    const state = currentVersion.attributes?.appStoreState;
    const isCurrentVersionEditable = ['PREPARE_FOR_SUBMISSION', 'READY_FOR_REVIEW', 'DEVELOPER_REJECTED', 'REJECTED'].includes(state);
    
    if (!isCurrentVersionEditable) return false;
    
    // Check if any version is in a state that prevents app info editing
    // Only restrict if there are versions in review/approved states AND no editable version exists
    if (!versions || versions.length === 0) return true;
    
    const restrictedStates = [
      'WAITING_FOR_REVIEW',
      'IN_REVIEW', 
      'PENDING_DEVELOPER_RELEASE',
      'READY_FOR_SALE',
      'REPLACED_WITH_NEW_VERSION',
      'PENDING_APPLE_RELEASE'
    ];
    
    const hasRestrictedVersion = versions.some(version => 
      restrictedStates.includes(version.attributes?.appStoreState)
    );
    
    // Allow editing if current version is editable, even if other versions are restricted
    // This matches App Store Connect behavior where you can edit app info as long as you have a version in preparation
    return isCurrentVersionEditable;
  };

  const handleLocaleChange = (locale) => {
    setSelectedLocale(locale);
    const localization = localizations.find(loc => loc.attributes.locale === locale);
    if (localization) {
      setFormData({
        name: localization.attributes.name || '',
        subtitle: localization.attributes.subtitle || '',
        privacyPolicyUrl: localization.attributes.privacyPolicyUrl || '',
        copyright: currentVersion?.attributes?.copyright || '',
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const localization = localizations.find(loc => loc.attributes.locale === selectedLocale);
      console.log('Selected locale:', selectedLocale);
      console.log('Found localization:', localization);
      console.log('Form data to save:', formData);
      
      // Update app info localization (name, subtitle, privacy policy)
      if (localization) {
        console.log('Updating localization with ID:', localization.id);
        await updateAppInfoLocalization(localization.id, {
          name: formData.name,
          subtitle: formData.subtitle,
          privacyPolicyUrl: formData.privacyPolicyUrl,
        });
      } else {
        console.error('No localization found for locale:', selectedLocale);
        alert('No localization found for the selected locale. Please select a locale that has been configured.');
        setSaving(false);
        return;
      }

      // Update version copyright (version-specific field)
      if (currentVersion && formData.copyright !== currentVersion.attributes?.copyright) {
        console.log('Updating version copyright:', formData.copyright);
        await updateAppStoreVersion(currentVersion.id, {
          copyright: formData.copyright
        });
      }

      alert('App information updated successfully!');
      onUpdate();
    } catch (err) {
      console.error('Error updating app information:', err);
      alert('Failed to update app information: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <p className="text-secondary" style={{ margin: 0 }}>
            App name, subtitle, and privacy policy
          </p>
          {appInfo && currentVersion && (
            <div className={`alert ${appInfo.attributes?.appStoreState === currentVersion.attributes?.appStoreState ? 'alert-info' : 'alert-warning'}`} style={{ marginTop: '12px', padding: '12px' }}>
              {appInfo.attributes?.appStoreState === currentVersion.attributes?.appStoreState ? (
                <>
                  ✓ Showing info for selected version: <strong>{currentVersion.attributes?.versionString}</strong> ({currentVersion.attributes?.appStoreState})
                </>
              ) : (
                <>
                  ⚠️ Selected version ({currentVersion.attributes?.versionString} - {currentVersion.attributes?.appStoreState}) info not available. 
                  Showing: <strong>{appInfo.attributes?.appStoreState || 'Unknown'}</strong>
                </>
              )}
            </div>
          )}
        </div>
        <button 
          onClick={() => loadAppInfo(true)} 
          className="btn btn-secondary"
          style={{ fontSize: '13px', padding: '6px 12px' }}
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      {currentVersion && !isAppInfoEditable() && (
        <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
          <span className="alert-icon">⚠️</span>
          <span>
            App information can only be edited when you have a version in "Prepare for Submission", "Ready for Review", "Developer Rejected", or "Rejected" state.
            Current version state: <strong>{currentVersion.attributes?.appStoreState}</strong>
          </span>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Language/Region</label>
        <select
          className="form-select"
          value={selectedLocale}
          onChange={(e) => handleLocaleChange(e.target.value)}
          disabled={!isAppInfoEditable()}
        >
          {localizations.map((loc, index) => {
            const localeInfo = AVAILABLE_LOCALES.find(l => l.code === loc.attributes.locale);
            const displayName = localeInfo ? `${localeInfo.name} (${loc.attributes.locale})` : loc.attributes.locale;
            const isPrimary = index === 0;
            return (
              <option key={loc.id} value={loc.attributes.locale}>
                {isPrimary ? '⭐ ' : ''}{displayName}{isPrimary ? ' - Primary' : ''}
              </option>
            );
          })}
        </select>
        <small className="text-secondary" style={{ display: 'block', marginTop: '8px' }}>
          {localizations.length} localization{localizations.length !== 1 ? 's' : ''} available • First locale is the primary/default
        </small>
        <small className="text-secondary" style={{ display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
          To add or remove localizations, go to Section 2: Version Details → Description
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">App Name</label>
        <input
          type="text"
          className="form-input"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          maxLength={30}
          disabled={!isAppInfoEditable()}
        />
        <small className="text-secondary">{formData.name.length}/30 characters</small>
      </div>

      <div className="form-group">
        <label className="form-label">Subtitle</label>
        <input
          type="text"
          className="form-input"
          value={formData.subtitle}
          onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
          maxLength={30}
          disabled={!isAppInfoEditable()}
        />
        <small className="text-secondary">{formData.subtitle.length}/30 characters</small>
      </div>

      <div className="form-group">
        <label className="form-label">Privacy Policy URL</label>
        <input
          type="url"
          className="form-input"
          placeholder="https://example.com/privacy"
          value={formData.privacyPolicyUrl}
          onChange={(e) => setFormData({ ...formData, privacyPolicyUrl: e.target.value })}
          disabled={!isAppInfoEditable()}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Copyright</label>
        <input
          type="text"
          className="form-input"
          placeholder="2025 Your Company Name"
          value={formData.copyright}
          onChange={(e) => setFormData({ ...formData, copyright: e.target.value })}
          disabled={!isAppInfoEditable()}
        />
        <small className="text-secondary">
          The copyright holder of your app. Example: "2025 Company Name" or "© 2025 Company Name"
        </small>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving || !isAppInfoEditable()}
      >
        {saving ? 'Saving...' : 'Save App Information'}
      </button>
    </div>
  );
}

export default AppInformation;
